// Shared bits for the Client Onboarding page (Nic 2026-09-28).

// The clock on a card: business days since the last touch (or since they
// were taken, or signed). Same palette as Signing Up: 1-2 blue, 3-4
// yellow, 5+ red.
export const businessDaysSince = (from, now = new Date()) => {
  if (!from) return null;
  const start = new Date(from);
  if (Number.isNaN(start.getTime())) return null;
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // A weekend start makes the next weekday day 1 (matches the server).
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  if (d > end) return 1;
  let days = 1; // the start day is day 1
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days += 1;
  }
  return days;
};

export const AGE_TONE = {
  fresh: {
    pill: 'bg-sky-500 text-white',
    label: 'Day 1-2',
  },
  aging: {
    pill: 'bg-amber-500 text-white',
    label: 'Day 3-4',
  },
  stale: {
    pill: 'bg-rose-600 text-white',
    label: 'Day 5+',
  },
};

export const ageOf = (days) => {
  if (days == null) return null;
  if (days <= 2) return 'fresh';
  if (days <= 4) return 'aging';
  return 'stale';
};

// Which moment the clock counts from, per section. VA.gov waiting is the
// client's time, not the specialist's (Nic 2026-10-05): a client who just
// became ready counts from then.
const later = (a, b) => (!a ? b : !b ? a : new Date(a) > new Date(b) ? a : b);
export const clockStartOf = (card) => {
  const base =
    card.state === 'pool'
      ? card.signedAt
      : card.state === 'reengaged'
        ? card.reengagedAt
        : card.state === 'nurture'
          ? card.nurturedAt
          : card.lastTouchAt || card.claimedAt || card.signedAt;
  return card.vaGov?.stage === 'ready' ? later(base, card.vaGov.readySince) : base;
};

// Waiting on VA.gov (Nic 2026-10-05): the "Nudge them" text shows once
// they have waited this many business days since signing, being sent back,
// or the last nudge.
export const NUDGE_AFTER_DAYS = 2;
export const nudgeDue = (vaGov, now = new Date()) => {
  if (!vaGov || vaGov.stage !== 'waiting') return false;
  const from = later(vaGov.waitingSince, vaGov.nudgedAt);
  const days = businessDaysSince(from, now);
  // The start day is day 1, so 2 full business days later is day 3.
  return days != null && days > NUDGE_AFTER_DAYS;
};

// The page's tabs (Nic 2026-10-05): the board, and Nurture. No Unassigned
// (every client has a specialist) and no Re-engaged pile (a client who
// reaches back out goes straight to a specialist, flagged "Came back").
export const SECTIONS = [
  // Clients (Nic 2026-10-11): Today, Board, Table, Week or Team inside it.
  { key: 'board', label: 'Clients', empty: 'Nobody on your board yet. New signups land here.' },
  { key: 'nurture', label: 'Stalled', empty: 'Nobody is stalled.' },
  // Every signed agreement, to correct and resend (Nic 2026-10-10).
  { key: 'signed', label: 'Signed SAs', empty: 'No signed agreements yet.' },
];

// The board, left to right (Nic 2026-10-10, round 3 step 5). Booking moves
// them to Booked; only "On with the client" moves them to Gathering
// records, never a checklist answer. Setting up VA.gov is a list above it.
export const COLUMNS = [
  {
    key: 'vaGov',
    name: 'Setting up VA.gov',
    job: 'Their step. Nudge after 2 business days; they stall with no movement, like everyone.',
    quiet: true,
  },
  {
    key: 'needsBooking',
    name: 'Needs booking',
    job: 'Book their first call. Only we book it.',
  },
  {
    key: 'booked',
    name: 'Booked',
    job: 'On the calendar. The checklist opens when the call starts.',
  },
  {
    key: 'records',
    name: 'Gathering records',
    job: 'DD-214, disability list, records, letters, address.',
  },
  {
    // Everything is in but their private treatment records (Nic 2026-10-26).
    key: 'ptrs',
    name: 'Waiting on PTRs',
    job: 'Only their private treatment records are left. Nudge as they near Stalled; review what they send.',
  },
  {
    key: 'readyForCm',
    name: 'Ready for CM',
    // A success footnote (Nic 2026-10-09): done here; CM Assignment takes it.
    job: 'Done here. Their Claims Manager is picked on CM Assignment.',
    done: true,
  },
];

export const REENGAGED_VIA = {
  inbound_text: 'texted us',
  inbound_call: 'called us',
  upload: 'uploaded a file',
  sign_in: 'signed in',
  va_gov_done: 'said VA.gov is done',
  admin: 'brought back by an admin',
};

// Who said VA.gov was ready (Nic 2026-10-05).
export const VA_READY_VIA = {
  sales: 'Sales Rep confirmed VA.gov',
  client: 'Says VA.gov is set up',
};

export const VA_GOV_LABEL = { yes: 'Yes', no: 'No', unsure: "I don't know" };

export const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10
    ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    : p || '';
};

export const THEME_KEY = 'coTheme';
// Ready for CM's lane shrunk to a rail (remembered per browser).
export const READY_SLIM_KEY = 'co.readyForCm.slim';

// Answers written at the launch cutover (and by Work the checklist) carry
// this name in their answerMeta instead of a person.
export const CARRIED_LABEL = 'Carried over at launch';
// Confirm service agreement is only for new clients signed in the app (Nic
// 2026-10-27): a carried-over client has it checked by default, and the
// checklist draws the step struck through with no VA check or Accept.
export const isCarriedSa = (step, answerMeta) => step?.id === 'sa_confirmed' && answerMeta?.sa_confirmed?.name === CARRIED_LABEL;
